import React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllCodes } from './store/actions/codeActions';
import { fetchUsers } from './store/actions/userActions';
import { fetchEntitlement } from './store/actions/entitlementActions';
import { watchSessionAcrossTabs } from './components/auth/v2/authV2Services';
import { useAuthSync } from './components/user/hooks/useAuthSync';
import { useTokenExpiration } from './components/user/hooks/useTokenExpiration';

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import CheckEmail from './components/auth/CheckEmail';
import VerifyEmail from './components/auth/VerifyEmail';
import AthleteOnboarding from './components/ForAthletes/v2/onboarding/AthleteOnboarding';
import CoachOnboarding from './components/ForCoaches/v2/onboarding/CoachOnboarding';
import CoachVerificationPage from './components/ForCoaches/v2/onboarding/CoachVerificationPage';
import WelcomePage from './components/auth/v2/WelcomePage';
import AccountTypePage from './components/auth/v2/AccountTypePage';
import LoginPage from './components/auth/v2/LoginPage';
import SignupEmailPage from './components/auth/v2/SignupEmailPage';
import SignupVerifyPage from './components/auth/v2/SignupVerifyPage';
import SignupNamePage from './components/auth/v2/SignupNamePage';
import SignupBirthdayPage from './components/auth/v2/SignupBirthdayPage';
import SignedOutOnly from './components/auth/v2/SignedOutOnly';
import ClassicRegisterRedirect from './components/auth/v2/ClassicRegisterRedirect';
import UnauthorizedPage from './components/auth/v2/UnauthorizedPage';
import { landingPathFor, billingPathFor, athleteProfilePathFor, ATHLETE_LANDING } from './components/auth/v2/landingPath';
import NotFound from './components/common/NotFound';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import ChangePassword from './components/auth/ChangePassword';
import AllAthletes from './components/ForBusiness/Dashboard/AllAthletes';
import PlayerIndexLayout from './components/ForBusiness/Dashboard/PlayerIndexLayout';
import PublicAthleteProfile from './components/PublicAthleteProfile';
import PublicAthletesDashboard from './components/PublicAthletesDashboard';
import PublicApiDocsPage from './components/PublicApiDocsPage';
import AthleteProfileView from './components/ForAthletes/v2/AthleteProfileView';
import AthleteEditHub from './components/ForAthletes/v2/edit/AthleteEditHub';
import AthleteModeLayout from './components/ForAthletes/v2/AthleteModeLayout';
import CoachModeLayout from './components/ForCoaches/v2/CoachModeLayout';
import CoachHomePage from './components/ForCoaches/v2/CoachHomePage';
import CoachTeamPage from './components/ForCoaches/v2/CoachTeamPage';
import CoachAllocationPage from './components/ForCoaches/v2/CoachAllocationPage';
import CoachCirclePage from './components/ForCoaches/v2/CoachCirclePage';
import CoachProfileHub from './components/ForCoaches/v2/profile/CoachProfileHub';
import RepModeLayout from './components/ForReps/v2/RepModeLayout';
import RepHomePage from './components/ForReps/v2/RepHomePage';
import RepAthletesPage from './components/ForReps/v2/RepAthletesPage';
import RepCirclePage from './components/ForReps/v2/RepCirclePage';
import SupporterModeLayout, { SUPPORTER_HOME } from './components/ForSupporters/v2/SupporterModeLayout';
import SupporterCirclePage from './components/ForSupporters/v2/SupporterCirclePage';
import AthleteHomePage from './components/ForAthletes/v2/AthleteHomePage';
import AthleteProfilePage from './components/ForAthletes/v2/AthleteProfilePage';
import AthleteCirclePage from './components/ForAthletes/v2/AthleteCirclePage';
import AthleteAtlasPage from './components/ForAthletes/v2/AthleteAtlasPage';
import AthleteDealsPage from './components/ForAthletes/v2/AthleteDealsPage';
import AthleteOfferPage from './components/ForAthletes/v2/AthleteOfferPage';
import AthleteCounterPage from './components/ForAthletes/v2/AthleteCounterPage';
import AthleteContractPage from './components/ForAthletes/v2/AthleteContractPage';
import AthleteTrackingPage from './components/ForAthletes/v2/AthleteTrackingPage';
import AthleteMessagesPage from './components/ForAthletes/v2/AthleteMessagesPage';
import AthleteBillingPage from './components/ForAthletes/Billing/AthleteBillingPage';
import CoachBillingPage from './components/ForBusiness/Billing/CoachBillingPage';
import CoachProfile from './components/ForBusiness/Profile/CoachProfile';
import ProtectedRoute from './components/auth/ProtectedRoute';
import CodeRelationPage from './components/code/CodeRelationPage';
import AdminUserManager from './components/user/UserManagement/AdminUserManager';
import AdminModeLayout from './components/admin/v2/AdminModeLayout';
import DataSyncComputeTab from './components/admin/DataSyncComputeTab';
import AnalyticsTab from './components/admin/AnalyticsTab';
import EmailCampaignsPage from './components/admin/EmailCampaignsPage';
import ImpersonateLanding from './components/admin/ImpersonateLanding';
import ImpersonationBanner from './components/admin/ImpersonationBanner';
import LogViewer from './components/auth/LogViewer';
import './index.css';
import { Toaster } from 'sonner';
import { useFitScale } from './components/ForBrands/useFitScale';
import ScrollToTop from './components/common/ScrollToTop';
import PromoCodeManager from './components/user/UserManagement/PromoCodeManager';
import AdminPaymentsPage from './components/admin/AdminPaymentsPage';
import TeamManagementPage from './components/admin/TeamManagementPage';
import TeamGamesAdminPage from './components/admin/TeamGamesAdminPage';
import Dependencies from './components/admin/Dependencies';
import ApiKeysAdminPage from './components/admin/ApiKeysAdminPage';
import CircleAthleteEdit from './components/Circle/CircleAthleteEdit';
import BrandModeLayout from './components/ForBrands/BrandModeLayout';
import HomePage from './components/ForBrands/HomePage';
import DiscoverPage from './components/ForBrands/DiscoverPage';
import ComparePage from './components/ForBrands/ComparePage';
import ShortlistPage from './components/ForBrands/ShortlistPage';
import CampaignsPage from './components/ForBrands/CampaignsPage';
import CampaignBuilderPage from './components/ForBrands/CampaignBuilderPage';
import CampaignManagePage from './components/ForBrands/CampaignManagePage';
import ComposeOfferPage from './components/ForBrands/ComposeOfferPage';
import ReviewCounterPage from './components/ForBrands/ReviewCounterPage';
import ContentApprovalPage from './components/ForBrands/ContentApprovalPage';
import MessagesPage from './components/ForBrands/MessagesPage';
import DealTrackingPage from './components/ForBrands/DealTrackingPage';

// The Player Index roles: "/" and /business/dashboard show them the index,
// and /business/password their password page, inside PlayerIndexLayout.
const PLAYER_INDEX_ROLES = ["IS_BRAND_AGENCY", "IS_ADMIN", "IS_CACHE_AI", "IS_COACH", "IS_RECRUITER_SCOUT", "IS_AD_PD"];
const ALL_ROLES = [...PLAYER_INDEX_ROLES, "IS_ATHLETE", "IS_INTERN", "IS_REP", "IS_SUPPORTER"];

// Who may open the business account pages, in the Player Index
// (/business/profile, /business/billing) or in Brand Mode (/brand/profile,
// /brand/billing). Coaches have theirs in Coach Mode.
const ACCOUNT_PROFILE_ROLES = ["IS_RECRUITER_SCOUT", "IS_BRAND_AGENCY", "IS_AD_PD", "IS_ADMIN"];
const ACCOUNT_BILLING_ROLES = ["IS_RECRUITER_SCOUT", "IS_BRAND_AGENCY", "IS_AD_PD"];

// Paths drawn inside the Player Index shell beyond "/" and the index itself:
// the athlete profile other roles open, Circle for the business roles, and
// the account's profile and billing pages. A trailing slash matches the same
// route, so it counts too.
const inPlayerIndexShell = (pathname = '') => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return path === '/business/dashboard' || path === '/business/password'
    || path === '/business/profile' || path === '/business/billing'
    || path === '/business/athletes' || path.startsWith('/business/athletes/')
    || path === '/circle' || path === '/circle/athlete/edit';
};

// A brand/agency account that is not an admin or Cache AI staff works in
// Brand Mode, so its profile and billing live there (landingPathFor sends
// the same accounts to /brand/discover).
const isPureBrand = (permissions = []) => permissions.includes("IS_BRAND_AGENCY")
  && !permissions.includes("IS_ADMIN") && !permissions.includes("IS_CACHE_AI");

// A redirect that keeps the query string, so a deep link (the editor's
// ?section=, a profile's ?tab=) survives the move to the new address.
const NavigateKeepingSearch = ({ to }) => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
};

// The old athlete profile link. Every caller keeps navigating to
// /business/athlette/profile/..., and each account lands on the athlete page
// inside the shell it works in (athleteProfilePathFor).
const AthleteProfileRedirect = ({ permissions }) => {
  const { athleteId } = useParams();
  return <NavigateKeepingSearch to={athleteProfilePathFor(permissions, athleteId)} />;
};

// The old Stripe return page. Athlete checkouts now come back to
// /athlete/billing, which shows the result; a checkout started before that
// change still lands there, with its session id.
const SubscriptionSuccessRedirect = () => {
  const { search } = useLocation();
  const sessionId = new URLSearchParams(search).get('session_id');
  const query = new URLSearchParams({ payment: 'success' });
  if (sessionId) query.set('session_id', sessionId);
  return <Navigate to={{ pathname: '/athlete/billing', search: `?${query.toString()}` }} replace />;
};

// V2 toasts: Inter type on a white card with the bm border, radius and ink
// text, with the success or critical colour on the icon only so the message
// stays readable. The `!` beats sonner's own [data-styled] rules.
const TOAST_CLASS_NAMES = {
  toast: '!font-inter !rounded-[12px] !border-bm-border !bg-white !text-bm-ink !shadow-[0_4px_16px_rgba(0,0,0,0.08)]',
  title: '!font-semibold',
  description: '!text-bm-muted',
  success: '[&_[data-icon]]:text-bm-positive',
  error: '[&_[data-icon]]:text-bm-critical',
  info: '[&_[data-icon]]:text-bm-info',
};

// Wrapper component to access `useLocation`
const AppContent = () => {
  useTokenExpiration();

  const location = useLocation();
  const token = localStorage.getItem('token');
  const { user } = useAuthSync();
  const permissions = user?.permissions || [];
  const landing = landingPathFor(permissions);
  const pureBrand = isPureBrand(permissions);
  // Every signed-in page sits in a V2 mode shell (Brand, Athlete, Coach, Rep,
  // Supporter, Admin and the Player Index: "/" for business roles,
  // /business/dashboard, /business/password, /business/profile,
  // /business/billing, /business/athletes and Circle), each with its own
  // topbar and tab bar, or draws its own V2 frame (sign-in and sign-up, the
  // public pages under /p, /unauthorized, /change-password, the 404 page),
  // or only redirects. The legacy navbar and sidebar are gone.
  const rootShowsPlayerIndex = !!token
    && !permissions.includes("IS_ATHLETE") && !permissions.includes("IS_INTERN")
    && !pureBrand
    && permissions.some((p) => PLAYER_INDEX_ROLES.includes(p));
  const inModeShell = location.pathname.startsWith('/brand') || location.pathname.startsWith('/athlete') || location.pathname.startsWith('/coach') || location.pathname.startsWith('/rep') || location.pathname.startsWith('/supporter')
    || location.pathname === '/users' || location.pathname.startsWith('/users/') || location.pathname.startsWith('/admin')
    || inPlayerIndexShell(location.pathname) || (location.pathname === '/' && rootShowsPlayerIndex)
    || location.pathname === '/cacheshare-tool';
  // Toasts follow the V2 shells' fit-to-width zoom so they are not tiny
  // next to a zoomed page.
  const fit = useFitScale();

  return (
    <div className="min-h-screen flex">
      <Toaster
        position="top-right"
        duration={1500}
        toastOptions={{ classNames: TOAST_CLASS_NAMES, style: { zoom: inModeShell ? fit.scale : 1 } }}
      />
      <div className="min-w-0 flex-1">
        <ImpersonationBanner />
        <Routes>
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          {/* New tab opened by an admin from the Users page: stores the user session for this tab only. */}
          <Route path="/admin/impersonate" element={<ImpersonateLanding />} />
          {/* A signed-in visitor goes to their home (landingPathFor: coaches
              to Coach Mode, pure brands to Brand Mode, other business roles
              to the Player Index, then athletes, interns, reps and
              supporters); without one they get the page itself. */}
          <Route path="/login" element={token && landing ? <Navigate to={landing} /> : <LoginPage />} />
          <Route path="/register" element={token && landing ? <Navigate to={landing} /> : <AccountTypePage />} />

          {/* Sign-up: /register picks the account type; every type continues
              through welcome, a code (or Google) and the name step, where the
              account is created (athletes add their birthday first). The
              classic form at /register/other is retired: old links open the
              type screen with their ?role= type picked. */}
          <Route path="/welcome" element={token ? <Navigate to="/" /> : <WelcomePage />} />
          <Route path="/register/other" element={token ? <Navigate to="/" /> : <ClassicRegisterRedirect />} />
          <Route path="/signup/email" element={<SignedOutOnly><SignupEmailPage /></SignedOutOnly>} />
          <Route path="/signup/verify" element={<SignedOutOnly><SignupVerifyPage /></SignedOutOnly>} />
          <Route path="/signup/name" element={<SignedOutOnly><SignupNamePage /></SignedOutOnly>} />
          <Route path="/signup/birthday" element={<SignedOutOnly><SignupBirthdayPage /></SignedOutOnly>} />
          <Route path="/check-email" element={<CheckEmail />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          {/* Public, no-login athlete share page. */}
          <Route path="/p/business/dashboard" element={<PublicAthletesDashboard />} />
          {/* Public Partner API docs — same content as the admin API Keys
              page's Documentation tab, readable without a login. Declared
              before /p/:athleteId so "api-docs" isn't taken for an id. */}
          <Route path="/p/api-docs" element={<PublicApiDocsPage />} />
          <Route path="/p/:athleteId" element={<PublicAthleteProfile />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/change-password" element={<ChangePassword />} />

          {/* Athletes and coaches each have their V2 wizard. An account
              holding both roles takes the athlete wizard, as the classic
              wizard did. */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute
                permissions={["IS_ATHLETE", "IS_COACH"]}
                skipOnboardingGate
              >
                {permissions.includes("IS_COACH") && !permissions.includes("IS_ATHLETE")
                  ? <CoachOnboarding />
                  : <AthleteOnboarding />}
              </ProtectedRoute>
            }
          />

          <Route
            path="/"
            element={
              token ? (
                permissions.includes("IS_ATHLETE") ? (
                  <Navigate to={ATHLETE_LANDING} />
                ) : permissions.includes("IS_INTERN") ? (
                  // Interns work in the Admin Mode shell's Users page.
                  <Navigate to="/users" replace />
                ) : pureBrand ? (
                  <Navigate to="/brand/discover" />
                ) : permissions.some(p => PLAYER_INDEX_ROLES.includes(p)) ? (
                  <ProtectedRoute permissions={PLAYER_INDEX_ROLES}>
                    <PlayerIndexLayout><AllAthletes /></PlayerIndexLayout>
                  </ProtectedRoute>
                ) : (
                  <Navigate to="/login" />
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          <Route
            path="/business/dashboard"
            element={
              <ProtectedRoute permissions={PLAYER_INDEX_ROLES}>
                <PlayerIndexLayout><AllAthletes /></PlayerIndexLayout>
              </ProtectedRoute>
            }
          />
          {/* The Player Index roles change their password inside its shell. */}
          <Route
            path="/business/password"
            element={
              <ProtectedRoute permissions={PLAYER_INDEX_ROLES}>
                <PlayerIndexLayout><ChangePassword /></PlayerIndexLayout>
              </ProtectedRoute>
            }
          />
          {/* Coach Mode V2 — Home, Team (roster / performance / NIL
              intelligence), CacheShare™ (allocation) and My Circle, plus the
              athlete profile, the coach profile and billing inside the coach
              shell. */}
          <Route path="/coach/dashboard" element={<Navigate to="/coach/team" replace />} />
          <Route
            path="/coach"
            element={
              <ProtectedRoute permissions={["IS_COACH", "IS_ADMIN", "IS_CACHE_AI"]}>
                <CoachModeLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/coach/home" replace />} />
            <Route path="home" element={<CoachHomePage />} />
            <Route path="team" element={<CoachTeamPage />} />
            <Route path="cacheshare" element={<CoachAllocationPage />} />
            <Route path="allocation" element={<Navigate to="/coach/cacheshare" replace />} />
            <Route path="circle" element={<CoachCirclePage />} />
            <Route path="athletes/:athleteId?" element={<AthleteProfileView />} />
            <Route path="profile" element={<CoachProfileHub />} />
            <Route path="billing" element={<CoachBillingPage />} />
            <Route path="password" element={<ChangePassword />} />
            {/* Where a coach waits for admin verification after onboarding. */}
            <Route path="verification" element={<CoachVerificationPage />} />
          </Route>
          {/* Rep Mode V2 — Home, Athletes (roster / performance / NIL
              intelligence over the athletes who linked the rep's Rep ID,
              and each athlete's profile) and My Circle, plus the rep's
              profile inside the rep shell. */}
          <Route
            path="/rep"
            element={
              <ProtectedRoute permissions={["IS_REP", "IS_ADMIN", "IS_CACHE_AI"]}>
                <RepModeLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/rep/home" replace />} />
            <Route path="home" element={<RepHomePage />} />
            <Route path="athletes" element={<RepAthletesPage />} />
            <Route path="athletes/:athleteId" element={<AthleteProfileView />} />
            <Route path="circle" element={<RepCirclePage />} />
            <Route path="profile" element={<CoachProfile />} />
            <Route path="password" element={<ChangePassword />} />
          </Route>
          {/* Supporters (parents, guardians, trainers): My Circle, with the
              athletes who added them and the contracts they co-sign, and the
              editor for an athlete who lets them edit, inside the supporter
              shell. */}
          <Route
            path="/supporter"
            element={
              <ProtectedRoute permissions={["IS_SUPPORTER", "IS_ADMIN", "IS_CACHE_AI"]}>
                <SupporterModeLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to={SUPPORTER_HOME} replace />} />
            <Route path="circle" element={<SupporterCirclePage />} />
            <Route path="athlete/edit" element={<CircleAthleteEdit />} />
            <Route path="password" element={<ChangePassword />} />
          </Route>
          {/* The athlete profile other roles open, inside the Player Index
              shell for the business roles (coaches, brands and reps have it
              in their own shells). The athlete is an ObjectId or an email. */}
          <Route
            path="/business/athletes/:athleteId?"
            element={
              <ProtectedRoute permissions={PLAYER_INDEX_ROLES}>
                <PlayerIndexLayout><AthleteProfileView /></PlayerIndexLayout>
              </ProtectedRoute>
            }
          />
          {/* The old athlete profile link, which every caller still uses:
              each account goes on to the athlete page in its own shell. */}
          <Route
            path="/business/athlette/profile/:athleteId?"
            element={
              <ProtectedRoute permissions={["IS_BRAND_AGENCY", "IS_ADMIN", "IS_CACHE_AI", "IS_COACH", "IS_RECRUITER_SCOUT", "IS_AD_PD", "IS_REP"]}>
                <AthleteProfileRedirect permissions={permissions} />
              </ProtectedRoute>
            }
          />
          {/* Athlete Mode V2 — Home, My Profile (+ the edit wizard), My Circle,
              Atlas, My Deals and the athlete side of the deal flow (offer,
              counter, contract, tracking, messages). One layout route holds
              the athlete shell and the AthleteModeProvider. */}
          {/* Athletes land on My Profile (ATHLETE_LANDING); /athlete/home is
              the Dashboard tab, which the old /athlete/dashboard address
              opens. */}
          <Route path="/athlete/dashboard" element={<Navigate to="/athlete/home" replace />} />
          <Route
            path="/athlete"
            element={
              <ProtectedRoute permissions={["IS_ATHLETE"]}>
                <AthleteModeLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to={ATHLETE_LANDING} replace />} />
            <Route path="home" element={<AthleteHomePage />} />
            <Route path="profile" element={<AthleteProfilePage />} />
            <Route path="profile/edit" element={<AthleteEditHub />} />
            <Route path="circle" element={<AthleteCirclePage />} />
            <Route path="atlas" element={<AthleteAtlasPage />} />
            <Route path="deals" element={<AthleteDealsPage />} />
            <Route path="deals/:dealId" element={<AthleteTrackingPage />} />
            <Route path="deals/:dealId/offer" element={<AthleteOfferPage />} />
            <Route path="deals/:dealId/counter" element={<AthleteCounterPage />} />
            <Route path="deals/:dealId/contract" element={<AthleteContractPage />} />
            <Route path="messages" element={<AthleteMessagesPage />} />
            <Route path="billing" element={<AthleteBillingPage />} />
            <Route path="password" element={<ChangePassword />} />
          </Route>
          {/* Circle — athletes manage who has access to their profile;
              members (parents / coaches / reps / trainers) see the athletes
              who added them and can open the editor their permission allows.
              Athletes, reps and supporters have it inside their V2 shells,
              interns work in Admin Mode, and the business roles get the V2
              page inside the Player Index shell. */}
          <Route
            path="/circle"
            element={
              permissions.includes("IS_ATHLETE") ? (
                <Navigate to="/athlete/circle" replace />
              ) : permissions.includes("IS_REP") && !permissions.includes("IS_ADMIN") ? (
                <Navigate to="/rep/circle" replace />
              ) : permissions.includes("IS_SUPPORTER") && !permissions.includes("IS_ADMIN") ? (
                <Navigate to={SUPPORTER_HOME} replace />
              ) : permissions.includes("IS_INTERN") && !permissions.some((p) => PLAYER_INDEX_ROLES.includes(p)) ? (
                <Navigate to="/users" replace />
              ) : (
                <ProtectedRoute permissions={["IS_SUPPORTER", "IS_COACH", "IS_ADMIN", "IS_CACHE_AI", "IS_INTERN", "IS_RECRUITER_SCOUT", "IS_BRAND_AGENCY", "IS_AD_PD"]}>
                  <PlayerIndexLayout><SupporterCirclePage eyebrow="Circle" /></PlayerIndexLayout>
                </ProtectedRoute>
              )
            }
          />
          {/* The editor a Circle member opens for an athlete: supporters in
              their shell, everyone else inside the Player Index shell. */}
          <Route
            path="/circle/athlete/edit"
            element={
              <ProtectedRoute permissions={["IS_ATHLETE", "IS_SUPPORTER", "IS_COACH", "IS_ADMIN", "IS_CACHE_AI", "IS_INTERN", "IS_RECRUITER_SCOUT", "IS_BRAND_AGENCY", "IS_AD_PD"]}>
                {permissions.includes("IS_SUPPORTER") && !permissions.includes("IS_ADMIN") ? (
                  <NavigateKeepingSearch to="/supporter/athlete/edit" />
                ) : (
                  <PlayerIndexLayout><CircleAthleteEdit /></PlayerIndexLayout>
                )}
              </ProtectedRoute>
            }
          />
          {/* Profile and billing for recruiters, AD/PD roles and admins
              (profile only), inside the Player Index shell. Pure brands have
              both in Brand Mode, so these send them there; coaches have them
              in the coach shell above. */}
          <Route
            path="/business/profile"
            element={
              <ProtectedRoute permissions={ACCOUNT_PROFILE_ROLES}>
                {pureBrand ? (
                  <Navigate to="/brand/profile" replace />
                ) : (
                  <PlayerIndexLayout><CoachProfile /></PlayerIndexLayout>
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/business/billing"
            element={
              <ProtectedRoute permissions={ACCOUNT_BILLING_ROLES}>
                {pureBrand ? (
                  <NavigateKeepingSearch to="/brand/billing" />
                ) : (
                  <PlayerIndexLayout><CoachBillingPage /></PlayerIndexLayout>
                )}
              </ProtectedRoute>
            }
          />
          {/* Retired intern and admin editors (old design): the Users page
              in Admin Mode replaces them, so old links land there. */}
          <Route path="/intern/player/profile/edit" element={<Navigate to="/users" replace />} />
          <Route path="/intern/player/circle/edit" element={<Navigate to="/users" replace />} />
          <Route path="/intern/coach/profile/edit" element={<Navigate to="/users" replace />} />
          <Route path="/intern/coach/dashboard" element={<Navigate to="/users" replace />} />
          <Route path="/intern/coach/team-players" element={<Navigate to="/users" replace />} />
          {/* Admin Mode V2 shell: Users for staff roles, plus every admin
              tool under /admin for admins. */}
          <Route
            path="/users"
            element={
              <ProtectedRoute permissions={["IS_ADMIN", "IS_CACHE_AI", "IS_COACH", "IS_INTERN"]}>
                <AdminModeLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminUserManager />} />
            <Route path="password" element={<ChangePassword />} />
          </Route>
          <Route
            path="/admin"
            element={
              <ProtectedRoute permissions={["IS_ADMIN"]}>
                <AdminModeLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/users" replace />} />
            <Route path="team-management" element={<TeamManagementPage />} />
            {/* A partner team's game stats sheet: box scores for the games
                Cerebro does not capture, entered by admins. */}
            <Route path="team-management/:teamId/games" element={<TeamGamesAdminPage />} />
            <Route path="data-sync" element={<DataSyncComputeTab />} />
            <Route path="analytics" element={<AnalyticsTab />} />
            <Route path="emails" element={<EmailCampaignsPage />} />
            <Route path="payments" element={<AdminPaymentsPage />} />
            <Route path="api-keys" element={<ApiKeysAdminPage />} />
            <Route path="promocodes" element={<PromoCodeManager />} />
            <Route path="dependencies" element={<Dependencies />} />
            <Route path="logs" element={<LogViewer />} />
            <Route path="codes" element={<CodeRelationPage />} />
            {/* Data Ingest was folded into Team Management; keep old links working. */}
            <Route path="data-ingest" element={<Navigate to="/admin/team-management" replace />} />
          </Route>
          {/* Codes moved into the Admin Mode shell. */}
          <Route path="/codes" element={<NavigateKeepingSearch to="/admin/codes" />} />
          <Route path="/logs" element={<Navigate to="/admin/logs" replace />} />
          {/* The retired payment history page: each account goes to its own
              billing page, or its home for roles without one. The guard waits
              for the account's roles before choosing. A coach awaiting
              verification may open billing, so the redirect skips that gate
              and leaves it to the destination's own guard. */}
          <Route
            path="/payments-history"
            element={
              <ProtectedRoute permissions={ALL_ROLES} skipCoachVerificationGate>
                <Navigate to={billingPathFor(permissions) || landing || '/'} replace />
              </ProtectedRoute>
            }
          />
          {/* Brand Mode V2 — Home, Discover (+ Compare / Shortlist), Campaigns
              and the deal flow (builder, manage, compose offer, counter review,
              content approval, messages, tracking), plus the brand's profile
              and billing. One layout route holds the brand shell and the
              BrandModeProvider. */}
          <Route
            path="/brand"
            element={
              <ProtectedRoute permissions={["IS_BRAND_AGENCY", "IS_ADMIN", "IS_CACHE_AI"]}>
                <BrandModeLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/brand/home" replace />} />
            <Route path="home" element={<HomePage />} />
            <Route path="discover" element={<DiscoverPage />} />
            <Route path="compare" element={<ComparePage />} />
            <Route path="shortlist" element={<ShortlistPage />} />
            <Route path="campaigns" element={<CampaignsPage />} />
            <Route path="campaigns/new" element={<CampaignBuilderPage />} />
            <Route path="campaigns/:campaignId" element={<CampaignManagePage />} />
            <Route path="campaigns/:campaignId/edit" element={<CampaignBuilderPage />} />
            <Route path="campaigns/:campaignId/messages" element={<MessagesPage />} />
            <Route path="campaigns/:campaignId/offer/:athleteId" element={<ComposeOfferPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="athletes/:athleteId?" element={<AthleteProfileView />} />
            {/* The same roles as /business/profile and /business/billing:
                the Brand Mode guard alone would also let Cache AI staff (and
                admins, for billing) in. */}
            <Route path="profile" element={<ProtectedRoute permissions={ACCOUNT_PROFILE_ROLES}><CoachProfile /></ProtectedRoute>} />
            <Route path="billing" element={<ProtectedRoute permissions={ACCOUNT_BILLING_ROLES}><CoachBillingPage /></ProtectedRoute>} />
            <Route path="password" element={<ChangePassword />} />
            <Route path="deals/:dealId" element={<DealTrackingPage />} />
            <Route path="deals/:dealId/counter" element={<ReviewCounterPage />} />
            <Route path="deals/:dealId/content/:submissionId" element={<ContentApprovalPage />} />
          </Route>
          {/* CacheShare tool: admins get the V2 allocation page inside the
              Admin Mode shell; coaches have the same page in Coach Mode. */}
          <Route
            path="/cacheshare-tool"
            element={
              <ProtectedRoute permissions={["IS_COACH", "IS_ADMIN"]}>
                {permissions.includes("IS_ADMIN") ? <AdminModeLayout /> : <Navigate to="/coach/cacheshare" replace />}
              </ProtectedRoute>
            }
          >
            <Route index element={<CoachAllocationPage />} />
          </Route>
          {/* The old payout estimator lives on as CacheShare in Coach Mode. */}
          <Route path="/estimate-payouts" element={<Navigate to="/coach/cacheshare" replace />} />
          {/* The old Stripe return page: athlete billing shows the result. */}
          <Route path="/subscription-success" element={<SubscriptionSuccessRedirect />} />
          {/* Any other address: the V2 404 page. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </div>
  );
};

// The full user list (users/whole/) answers admins and Cache AI staff only;
// the admin log viewer reads it.
export const canListAllUsers = (state) => {
  const permissions = state.user?.user?.permissions || [];
  return permissions.includes('IS_ADMIN') || permissions.includes('IS_CACHE_AI');
};

function App() {
  const dispatch = useDispatch();
  const listsUsers = useSelector(canListAllUsers);
  const signedInEmail = useSelector((state) => (state.user?.user?.email || '').toLowerCase());

  useEffect(() => {
    dispatch(fetchAllCodes());

    // Fetch user entitlement on app load if authenticated
    const token = localStorage.getItem('token');
    if (token) {
      dispatch(fetchEntitlement());
    }
  }, [dispatch]);

  // Admins and Cache AI staff only, once their account has loaded (it is not
  // persisted) and again when a different one signs in. Other accounts
  // would get a 403.
  useEffect(() => {
    if (listsUsers) dispatch(fetchUsers());
  }, [dispatch, listsUsers, signedInEmail]);

  // A log out, or a log in as someone else, in another tab reloads this one.
  useEffect(() => watchSessionAcrossTabs(), []);

  return (
    <>
      <Router>
        <ScrollToTop>
          <AppContent />
        </ScrollToTop>
      </Router>
    </>    
  );
}

export default App;
